import React, { useState, createContext, useContext } from 'react';
import { cn } from '../../utils/cn';

const TabsContext = createContext(null);

export function Tabs({ defaultValue, className, children, ...props }) {
    const [activeTab, setActiveTab] = useState(defaultValue || '');

    return (
        <TabsContext.Provider value={{ activeTab, setActiveTab }}>
            <div className={cn('flex flex-col gap-2', className)} {...props}>
                {children}
            </div>
        </TabsContext.Provider>
    );
}

export function TabsList({ className, children, ...props }) {
    return (
        <div
            className={cn(
                'bg-slate-100 text-slate-600 inline-flex h-9 w-fit items-center justify-center rounded-lg p-1',
                className
            )}
            {...props}
        >
            {children}
        </div>
    );
}

export function TabsTrigger({ value, className, children, ...props }) {
    const context = useContext(TabsContext);
    if (!context) throw new Error('TabsTrigger must be used within Tabs');

    const { activeTab, setActiveTab } = context;
    const isActive = activeTab === value;

    return (
        <button
            type="button"
            onClick={() => setActiveTab(value)}
            className={cn(
                'inline-flex h-[calc(100%-2px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-3 py-1 text-sm font-medium whitespace-nowrap transition-all',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200',
                'disabled:pointer-events-none disabled:opacity-50',
                isActive ? 'bg-white text-slate-900 shadow-sm border-slate-200' : 'text-slate-600 hover:text-slate-900',
                className
            )}
            {...props}
        >
            {children}
        </button>
    );
}

export function TabsContent({ value, className, children, ...props }) {
    const context = useContext(TabsContext);
    if (!context) throw new Error('TabsContent must be used within Tabs');

    const { activeTab } = context;
    if (activeTab !== value) return null;

    return (
        <div className={cn('flex-1 outline-none', className)} {...props}>
            {children}
        </div>
    );
}
